/**
 * useLLMComparison Hook - Multi-LLM Ensemble Consensus Data
 *
 * Sprint 3 Task 5 - Multi-LLM Comparison Dashboard Integration
 *
 * Fetches multi-LLM comparison data from backend for AI transparency.
 *
 * FERP Compliance:
 * - ✅ Data-Driven: All LLM comparison from backend GraphQL query
 * - ✅ Repository Pattern: Uses Apollo Client for data access
 * - ✅ Backend-Only Data: Frontend never generates comparison data
 * - ✅ Life-Critical: Multi-LLM validation for safety-critical templates
 * - ✅ Domain-Agnostic: Works across all AI-generated workflows
 */

import { useQuery } from '@apollo/client/react';
import { gql } from '@apollo/client';

// ============================================================================
// GraphQL QUERY
// ============================================================================

const GET_LLM_COMPARISON_DATA = gql`
  query GetLLMComparisonData($tenant: String!, $templateId: String!) {
    getLlmComparisonData(tenant: $tenant, templateId: $templateId) {
      templateId
      providers {
        provider
        overallConfidence
        fieldExtractions {
          fieldName
          value
          confidence
        }
        processingTimeMs
      }
      consensusFields {
        fieldName
        agreedProviders
        totalProviders
        consensusValue
        outliers {
          provider
          value
          confidence
        }
      }
      confidenceDistribution {
        mean
        median
        stdDev
        min
        max
      }
    }
  }
`;

// ============================================================================
// TypeScript INTERFACES (matches backend GraphQL schema)
// ============================================================================

export interface FieldExtraction {
  fieldName: string;
  value: any; // JSON value
  confidence: number;
}

export interface LLMProviderComparison {
  provider: string; // "claude", "gpt4", "gemini"
  overallConfidence: number;
  fieldExtractions: FieldExtraction[];
  processingTimeMs: number;
}

export interface OutlierValue {
  provider: string;
  value: any; // JSON value
  confidence: number;
}

export interface FieldConsensus {
  fieldName: string;
  agreedProviders: number;
  totalProviders: number;
  consensusValue: any; // JSON value
  outliers: OutlierValue[];
}

export interface ConfidenceDistribution {
  mean: number;
  median: number;
  stdDev: number;
  min: number;
  max: number;
}

export interface LLMComparisonData {
  templateId: string;
  providers: LLMProviderComparison[];
  consensusFields: FieldConsensus[];
  confidenceDistribution: ConfidenceDistribution;
}

export interface UseLLMComparisonParams {
  tenant: string;
  templateId: string;
  skip?: boolean; // Skip query if template not AI-generated
}

export interface UseLLMComparisonResult {
  data: LLMComparisonData | null;
  loading: boolean;
  error: Error | null;
  refetch: () => void;
}

// ============================================================================
// HOOK
// ============================================================================

/**
 * Hook to fetch multi-LLM comparison data for AI-generated templates
 *
 * Usage:
 * ```tsx
 * const { data, loading, error } = useLLMComparison({
 *   tenant: 'test',
 *   templateId: 'patient-admission',
 *   skip: !isAIGenerated // Only query for AI-generated templates
 * });
 * ```
 */
export function useLLMComparison(params: UseLLMComparisonParams): UseLLMComparisonResult {
  const { tenant, templateId, skip = false } = params;

  const { data, loading, error, refetch } = useQuery<Record<string, any>>(GET_LLM_COMPARISON_DATA, {
    variables: {
      tenant,
      templateId,
    },
    skip,
    fetchPolicy: 'cache-first', // Cache LLM comparison data (doesn't change often)
  });

  return {
    data: data?.getLlmComparisonData || null,
    loading,
    error: error || null,
    refetch,
  };
}
